import { z } from "zod";
import { stripOriginSearch } from "./navigationOriginKeys";
import { asWorkOrderId, type WorkOrderId } from "./workIds";
import type { WorkSearch } from "./workSearch";

// Every list and date falls back to unset when malformed, so a stale or
// hand-edited link still opens the list instead of failing validation.
const filterSchema = z
  .array(z.string().trim().min(1).max(120))
  .max(100)
  .optional()
  .catch(undefined);
const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  })
  .optional()
  .catch(undefined);

const ASSIGNEE_CODE = /^(user|team)-\d+$/;
// Keeps the well-formed user and team codes and drops the rest.
const assigneesSchema = z
  .array(z.unknown())
  .transform((values) =>
    values.filter(
      (value): value is string =>
        typeof value === "string" && ASSIGNEE_CODE.test(value),
    ),
  )
  .pipe(z.array(z.string()).max(100))
  .optional()
  .catch(undefined);

/** Filters that earlier links may still carry; they no longer exist. */
const RETIRED_FILTER_KEYS = [
  "workOrderPlannerGroups",
  "workOrderCompletedBy",
  "workOrderCreatedBy",
] as const;

export function stripRetiredWorkOrderFilters(
  search: Record<string, unknown>,
): Record<string, unknown> {
  const stripped = { ...search };
  for (const key of RETIRED_FILTER_KEYS) delete stripped[key];
  return stripped;
}

export const workOrdersSearchSchema = z
  .object({
    // A stale view in a saved link (for example the retired
    // "needs-planning") falls back to the default view instead of failing.
    workOrderView: z
      .enum(["mine", "created", "open", "done"])
      .optional()
      .catch(undefined),
    workOrderSearch: z.string().max(500).optional(),
    workOrderPage: z.coerce.number().int().min(1).max(10000).optional(),
    workOrderSelectedId: z.string().uuid().transform(asWorkOrderId).optional(),
    workOrderFullPage: z.boolean().optional(),
    workOrderPriorities: filterSchema,
    workOrderStatuses: filterSchema,
    workOrderTypes: filterSchema,
    workOrderAssetIds: filterSchema,
    workOrderCategoryIds: filterSchema,
    workOrderAssignees: assigneesSchema,
    workOrderDueDateFrom: dateSchema,
    workOrderDueDateTo: dateSchema,
  })
  .passthrough();

export type WorkOrdersSearch = z.infer<typeof workOrdersSearchSchema>;

export type WorkOrdersFilterSearch = Pick<
  WorkOrdersSearch,
  | "workOrderPriorities"
  | "workOrderStatuses"
  | "workOrderTypes"
  | "workOrderAssetIds"
  | "workOrderCategoryIds"
  | "workOrderAssignees"
>;

export type WorkOrdersLocationSearch = WorkSearch & WorkOrdersSearch;

// Both builders drop cross-module origin keys: a back link belongs only to
// the page the user arrived on, never to the list or record links built
// from that page's search.
export function buildWorkOrderDetailSearch(
  previousSearch: WorkOrdersLocationSearch,
  scopeSearch: WorkSearch,
): WorkOrdersLocationSearch {
  const detailSearch = stripOriginSearch(previousSearch);
  delete detailSearch.workOrderSelectedId;
  return {
    ...detailSearch,
    ...scopeSearch,
  };
}

export function buildWorkOrdersReturnSearch(
  originSearch: WorkOrdersLocationSearch | undefined,
  fallbackScopeSearch: WorkSearch,
  selectedWorkOrderId?: WorkOrderId,
): WorkOrdersLocationSearch {
  return {
    ...fallbackScopeSearch,
    ...(originSearch ? stripOriginSearch(originSearch) : {}),
    ...(selectedWorkOrderId
      ? { workOrderSelectedId: selectedWorkOrderId }
      : {}),
  };
}
